import NOTIFICATIONS from "../../constants/notifications";
import { withGuard } from "../../helpers/middleware";
import { setElementVisibility } from "../../helpers/dom-manipulation/dom-manipulation";
import { debounce } from "../../helpers/time";
import createNotificationsApi from "./api";
import { swiperOptionsVertical } from "../lux-swiper/options";
import {
    migrateSwiper,
    getSlideDataId,
    getNumberOfLoadedSlides,
    getMostRecentSlideId,
    getFullyVisibleSlides,
} from "../lux-swiper/helpers";

/**
 * Manages the notifications dropdown — loading, displaying, scrolling,
 * and marking notifications as read via a vertical Swiper instance.
 */
export class LuxNotifications {
    /**
     * Create a LuxNotifications instance and bind the account-panel listener.
     *
     * @param {string} swiperId - The DOM id of the notifications swiper container.
     * @param {string} csrf - CSRF token for authenticated API requests.
     */
    constructor(swiperId, csrf) {
        // Initialize properties
        this.swiperId = swiperId;
        this.api = createNotificationsApi(csrf);
        /** @type {import('../lux-swiper/LuxSwiper').swiperScroll | null} */
        this.notificationsSlider = null;
        /** @type {string[]} */
        this._initialNotifications = [];
        /** @type {Record<string, any>} */
        this.readTimers = {};
        this.notificationsCount = -1;
        this.notificationsSlideProgress = -1;
        this.translate = 0;
        this.markAllAsReadBound = false;

        const accountPanel = document.querySelector(NOTIFICATIONS.accountPanel);

        if (!accountPanel) {
            return;
        }

        accountPanel.addEventListener("click", async () => {
            const swiperWrapper = document.querySelector(
                NOTIFICATIONS.swiperWrapper,
            );

            if (!swiperWrapper) {
                return;
            }

            const unreadAmount = document.querySelector(
                NOTIFICATIONS.unreadAmount,
            );

            this.bindMarkAllAsRead(unreadAmount);

            await this.preloadNotificationsData();

            if (this.notificationsSlider) {
                /** @type {import('../lux-swiper/LuxSwiper').swiperScroll} */ (
                    this.notificationsSlider
                )._swiper?.destroy(true, true);
                this.notificationsSlider = null;
                swiperWrapper.innerHTML = "";
                this.translate = 0;
                this.notificationsSlideProgress = -1;
            }

            await this.initNotificationsSlider(swiperWrapper, unreadAmount);
        });
    }

    /**
     * Bind the "mark all as read" click listener once its element is in the DOM.
     *
     * @param {Element | null} unreadAmount - The unread-count badge element, if present.
     * @returns {void}
     */
    bindMarkAllAsRead(unreadAmount) {
        if (this.markAllAsReadBound) {
            return;
        }

        const markAllAsRead = document.getElementById(
            NOTIFICATIONS.markAllAsRead,
        );

        if (!markAllAsRead) {
            return;
        }

        this.markAllAsReadBound = true;

        markAllAsRead.addEventListener("click", () =>
            this.handleMarkAllNotifications(markAllAsRead, unreadAmount),
        );
    }

    /**
     * Whether the slider has loaded all available notifications.
     *
     * @returns {boolean} True if all notifications have been loaded.
     */
    get reachedEndOfAllNotifications() {
        return this.notificationsCount <= this.notificationsSlideProgress;
    }

    /**
     * Fetch the initial batch of notifications from the API and store them.
     *
     * @returns {Promise<void>}
     */
    async preloadNotificationsData() {
        const { notifications, notificationsCount } =
            await this.api.fetchInitialNotifications();

        this._initialNotifications = notifications;
        this.notificationsCount = notificationsCount;
    }

    /**
     * Initialise (or restore) the notifications vertical swiper and bind events.
     *
     * @param {Element} swiperWrapper - The swiper wrapper DOM element.
     * @param {Element | null} unreadAmount - The unread-count badge element, if present.
     * @returns {Promise<void>}
     */
    async initNotificationsSlider(swiperWrapper, unreadAmount) {
        if (!this.notificationsSlider) {
            this.notificationsSlider = new swiperScroll(
                this.swiperId,
                swiperOptionsVertical,
            );

            const slider =
                /** @type {import('../lux-swiper/LuxSwiper').swiperScroll} */ (
                    this.notificationsSlider
                );

            await withGuard(
                async () =>
                    this.handleLoading(
                        swiperWrapper,
                        this._initialNotifications.length,
                    ),
                () => setElementVisibility(NOTIFICATIONS.loader),
            )();

            if (!this._initialNotifications.length) return;

            this.notificationsSlideProgress = getNumberOfLoadedSlides(
                /** @type {{ _swiper: { slides: ArrayLike<any> } }} */ (
                    /** @type {unknown} */ (slider)
                ),
            );

            /** @type {import('swiper/bundle').default} */ (slider._swiper).on(
                "reachEnd",
                withGuard(
                    () => !this.reachedEndOfAllNotifications,
                    () => this.handleReachEnd(swiperWrapper),
                ),
            );

            /** @type {import('swiper/bundle').default} */ (slider._swiper).on(
                "setTranslate",
                debounce(() => this.handleSetTranslate(unreadAmount)),
            );
        } else {
            this.notificationsSlider.updateTranslate(this.translate);
        }
    }

    /**
     * Handle initial loading — show empty state or migrate pre-fetched slides.
     *
     * @param {Element} swiperWrapper - The swiper wrapper DOM element.
     * @param {number} notifications - The number of initial notifications loaded.
     * @returns {Promise<void>}
     */
    async handleLoading(swiperWrapper, notifications) {
        if (!notifications) {
            setElementVisibility(NOTIFICATIONS.empty, true);
        } else {
            migrateSwiper(this._initialNotifications, swiperWrapper);
            if (this.notificationsSlider) {
                this.notificationsSlider.updateTranslate(this.translate);
            }
        }
        return;
    }

    /**
     * Load more notifications when the swiper reaches its end.
     *
     * @param {Element} swiperWrapper - The swiper wrapper DOM element.
     * @returns {Promise<void>}
     */
    async handleReachEnd(swiperWrapper) {
        if (!this.notificationsSlider) return;
        const uid = getMostRecentSlideId(
            /** @type {{ _swiper: { slides: ArrayLike<any> } }} */ (
                /** @type {unknown} */ (this.notificationsSlider)
            ),
        );
        if (!uid) return;
        const { notifications } = await this.api.fetchMoreNotifications(uid);
        migrateSwiper(notifications, swiperWrapper);
        this.notificationsSlider.update();

        this.notificationsSlideProgress = getNumberOfLoadedSlides(
            /** @type {{ _swiper: { slides: ArrayLike<any> } }} */ (
                /** @type {unknown} */ (this.notificationsSlider)
            ),
        );
    }

    /**
     * Track the current translate and mark visible slides as read.
     *
     * @param {Element | null} unreadAmount - The unread-count badge element, if present.
     * @returns {Promise<void>}
     */
    async handleSetTranslate(unreadAmount) {
        if (!this.notificationsSlider) return;
        this.translate = this.notificationsSlider.getCurrentTranslate();

        const slidesToProcess = getFullyVisibleSlides(
            [NOTIFICATIONS.fullyVisible, NOTIFICATIONS.visible],
            this.reachedEndOfAllNotifications,
        );

        slidesToProcess.forEach((slide) =>
            this.processSlideAsRead(slide, unreadAmount),
        );
    }

    /**
     * Mark a single slide's notification as read via the API.
     *
     * @param {Element} slide - The slide element to process.
     * @param {Element | null} unreadAmount - The unread-count badge element, if present.
     * @returns {Promise<void>}
     */
    async processSlideAsRead(slide, unreadAmount) {
        const uid = getSlideDataId(slide.parentElement);
        if (!uid) throw new Error("UID is null or undefined");

        const { success, unreadCountText } =
            await this.api.fetchMarkNotificationAsRead(uid);

        if (success) {
            slide.classList.remove("unread");
            this.updateUnreadAmount(unreadAmount, unreadCountText);
        }
    }

    /**
     * Update the unread badge text or hide it when the count drops below 1.
     *
     * @param {Element | null} unreadAmount - The unread-count badge element, if present.
     * @param {number | string} unreadCountText - The new unread count value.
     * @returns {void}
     */
    updateUnreadAmount(unreadAmount, unreadCountText) {
        if (!unreadAmount) return;

        if (Number(unreadCountText) < 1) {
            /** @type {HTMLElement} */ (unreadAmount).hidden = true;
        } else {
            unreadAmount.innerHTML = String(unreadCountText);
        }
    }

    /**
     * Mark all notifications as read, close the dropdown, and hide the badge.
     *
     * @param {HTMLElement} markAllAsRead - The "mark all as read" button element.
     * @param {Element | null} unreadAmount - The unread-count badge element, if present.
     * @returns {Promise<void>}
     */
    async handleMarkAllNotifications(markAllAsRead, unreadAmount) {
        const { success } = await this.api.fetchMarkAllNotificationsAsRead();

        if (success) {
            markAllAsRead
                .closest(".lux-dropdown-wrapper")
                ?.classList.remove("active");

            document
                .querySelectorAll(".lux-notification-slide")
                .forEach((slide) => slide.classList.remove("unread"));

            if (unreadAmount) {
                /** @type {HTMLElement} */ (unreadAmount).hidden = true;
            }
        }
    }
}
